// Renders a structured `netwatch-table` block emitted by the assistant.
//
// The assistant returns JSON (in a ```netwatch-table fence) rather than a
// markdown table, so a column can be typed `severity` and rendered with the
// shaped SeverityIcon instead of a colour-only dot. Shape of the JSON:
//
//   {
//     "title": "optional caption",
//     "columns": [
//       { "key": "filesystem", "label": "Filesystem" },
//       { "key": "status", "label": "Status", "type": "severity" },
//       { "key": "usage", "label": "Usage" }
//     ],
//     "rows": [
//       { "filesystem": "(G:)", "status": 5, "usage": "92% of 245 GB" },
//       { "filesystem": "(H:)", "status": "ok", "usage": "74% of 120 GB" }
//     ]
//   }
//
// Grouped tables: a column tagged `"group": true` belongs to a parent entry;
// consecutive rows sharing the same `"group": "<key>"` value merge those cells
// (rowspan) so the parent shows once with its sub-rows beneath. Group-leading
// values are read from the first row of each run; continuation rows may omit
// them. Rows without a `group` key render as ordinary single rows, so existing
// flat tables are unaffected.

import { SeverityBadge, InlineSeverity } from './Severity'

// --- Table JSON shape (a dynamic boundary: parsed from assistant output) ---

/** A column spec; the assistant may also emit a bare string (shorthand). */
interface TableColumn {
  key: string
  label?: string
  type?: string
  group?: boolean
}

/** One data row: open map of column key -> cell value, plus an optional
 *  `group` key carrying the grouping value. */
type TableRow = Record<string, unknown> & { group?: unknown }

export interface TableData {
  title?: string
  columns: Array<TableColumn | string>
  rows: TableRow[]
}

interface CellProps {
  col: TableColumn
  value: unknown
  rowSpan?: number
  groupLead?: boolean
}

function Cell({ col, value, rowSpan, groupLead }: CellProps) {
  const cls = groupLead ? 'nw-ntable-group-cell' : undefined
  if (col.type === 'severity') {
    // No severity (e.g. no active Zabbix problem) → no icon, just a dash.
    if (value === null || value === undefined || value === '') {
      return <td rowSpan={rowSpan} className={cls}>—</td>
    }
    const sevCls = ['nw-ntable-cell-sev', cls].filter(Boolean).join(' ')
    return <td rowSpan={rowSpan} className={sevCls}><SeverityBadge level={value} /></td>
  }
  if (value === null || value === undefined || value === '') {
    return <td rowSpan={rowSpan} className={cls}>—</td>
  }
  // Free-text cells may carry an inline severity marker ("🔴 99% used") — render
  // those as shaped icons too, so per-metric dots aren't left as emoji.
  return <td rowSpan={rowSpan} className={cls}><InlineSeverity text={String(value)} /></td>
}

interface NetwatchTableProps {
  data: TableData | null | undefined
  raw?: string
}

interface RowMeta {
  start: boolean
  span: number
  startIdx: number
}

export function NetwatchTable({ data, raw }: NetwatchTableProps) {
  // Parse failed but the block looked complete → show the raw text rather than
  // silently dropping content.
  if (!data) {
    return <pre className="nw-ntable-raw">{raw}</pre>
  }
  const isEmpty = (v: unknown) => v === null || v === undefined || v === ''
  const cols: TableColumn[] = data.columns
    .map((c) => (typeof c === 'string' ? { key: c, label: c } : c))
    // Drop a severity column entirely when no row has a status (no active
    // Zabbix problem) — an all-dashes column adds nothing. Non-severity
    // columns are always kept (a present-but-empty column can be meaningful).
    .filter((c) => c.type !== 'severity' || data.rows.some((r) => !isEmpty(r[c.key])))

  const rows = data.rows
  const hasGroupCols = cols.some((c) => c.group)

  // Per-row grouping metadata: whether the row starts a group, its rowspan,
  // and the index of the group-start row (to read group-leading values from).
  const meta: RowMeta[] = rows.map((_, i) => ({ start: true, span: 1, startIdx: i }))
  if (hasGroupCols) {
    let i = 0
    while (i < rows.length) {
      const g = rows[i].group
      if (g === undefined || g === null || g === '') {
        meta[i] = { start: true, span: 1, startIdx: i }
        i += 1
        continue
      }
      let j = i + 1
      while (j < rows.length && rows[j].group === g) j += 1
      for (let k = i; k < j; k++) {
        meta[k] = { start: k === i, span: j - i, startIdx: i }
      }
      i = j
    }
  }

  // Every table is numbered: a leading `#` column the UI owns (so the model
  // never hand-numbers). Flat tables number each row 1..N; grouped tables
  // number per group (the number spans the group's sub-rows).
  let groupCounter = 0
  const rowNum = rows.map((_, ri) => {
    if (hasGroupCols) {
      if (meta[ri].start) groupCounter += 1
      return groupCounter
    }
    return ri + 1
  })

  return (
    <div className="nw-ntable-wrap">
      {data.title && <div className="nw-ntable-title">{data.title}</div>}
      <table className="nw-ntable">
        <thead>
          <tr>
            <th className="nw-ntable-num">#</th>
            {cols.map((c, i) => <th key={i}>{c.label ?? c.key}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const m = meta[ri]
            const numCell = hasGroupCols
              ? (m.start
                  ? <td className="nw-ntable-num nw-ntable-group-cell"
                        rowSpan={m.span > 1 ? m.span : undefined}>{rowNum[ri]}</td>
                  : null)
              : <td className="nw-ntable-num">{rowNum[ri]}</td>
            return (
              <tr key={ri} className={hasGroupCols && m.start ? 'nw-ntable-group-start' : undefined}>
                {numCell}
                {cols.map((c, ci) => {
                  if (c.group && hasGroupCols) {
                    // Group-leading cell: render once on the start row (spanning
                    // the group); continuation rows omit it (rowspan covers it).
                    if (!m.start) return null
                    return (
                      <Cell key={ci} col={c} value={rows[m.startIdx][c.key]}
                            rowSpan={m.span > 1 ? m.span : undefined} groupLead />
                    )
                  }
                  return <Cell key={ci} col={c} value={row[c.key]} />
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
